import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Lock,
  CheckCircle2,
  Globe2,
  Layers,
  Radio,
  Disc3,
  Users,
  Shield,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const BENEFITS = [
  { icon: Globe2, title: "Full Agency Directory", desc: "Unlimited access to the searchable directory of 200+ rights agencies — every PRO, IP authority, publisher, and digital aggregator." },
  { icon: Layers, title: "Master Rights Hub", desc: "Complete index of master rights administrators, sync agencies, and master catalog services." },
  { icon: Radio, title: "Digital Rights Hub", desc: "MLC, Section 115, DSP licensing partners, and global digital aggregators in one place." },
  { icon: Disc3, title: "Recording Rights Hub", desc: "Neighbouring rights societies, performer unions, and master collection bodies worldwide." },
  { icon: Users, title: "Unlimited Networking", desc: "Connect with composers, songwriters, publishers, producers, and arrangers globally." },
  { icon: Shield, title: "Lifetime Access", desc: "One-time payment. Yours forever. No subscriptions, no recurring fees." },
];

export default function Upgrade() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const startCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: "complete_access",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start checkout");
      setLoading(false);
    }
  };

  if (user?.has_complete_access) {
    return (
      <div className="min-h-screen bg-[#0A1128] pt-32 pb-20">
        <div className="max-w-2xl mx-auto px-6 text-center" data-testid="already-unlocked">
          <CheckCircle2 className="w-16 h-16 text-[#7DA3D9] mx-auto mb-6" />
          <h1 className="font-display text-4xl lg:text-5xl">You already have Complete Access</h1>
          <p className="text-slate-300 mt-4 leading-relaxed">
            Thank you for supporting OSRR. Every section of the registry is unlocked.
          </p>
          <Link to="/agencies" className="btn-gold inline-flex items-center gap-2 mt-8" data-testid="go-to-directory">
            Open the directory <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1128] pt-28 pb-20" data-testid="upgrade-page">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#7DA3D9]/25 bg-[#7DA3D9]/5 mb-6">
              <Lock className="w-3.5 h-3.5 text-[#7DA3D9]" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#B8D0EE] font-semibold">
                Complete Access · Lifetime
              </span>
            </div>
            <h1 className="font-display text-4xl lg:text-7xl tracking-tight leading-[0.98]">
              Unlock every<br />
              <span className="text-[#7DA3D9]">overseas right</span>.
            </h1>
            <p className="text-slate-300 mt-8 text-lg leading-relaxed max-w-2xl">
              For a one-time <span className="text-white font-semibold">$6.95</span> payment, gain
              lifetime access to the complete OSRR directory — every agency, every territory, every
              rights category. Stripe checkout supports cards & wallets from all 195+ countries,
              billed in your local currency. No subscriptions. Cancel-proof. Yours forever.
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="glass-card p-8 lg:p-10 relative overflow-hidden" data-testid="pricing-card">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#7DA3D9] to-transparent" />
              <div className="overline text-[#7DA3D9]">OSRR Complete Access</div>
              <div className="flex items-baseline gap-2 mt-4">
                <span className="font-display text-7xl text-white">$6.95</span>
                <span className="text-slate-400 text-sm">USD · one-time</span>
              </div>
              <div className="overline mt-1">Lifetime · No renewals</div>

              <ul className="mt-8 space-y-3.5 text-sm">
                {[
                  "200+ agencies, fully searchable & filterable",
                  "All 3 Rights Hubs (Master, Digital, Recording)",
                  "Full contact details — phone, address, websites",
                  "Unlimited networking with industry professionals",
                  "Personal favorites library — bookmark any of the 200+ agencies",
                  "Lifetime updates to the registry",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3 text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-[#7DA3D9] mt-0.5 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={startCheckout}
                disabled={loading}
                className="btn-gold w-full mt-8 inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="checkout-btn"
              >
                {loading ? "Redirecting to checkout…" : "Unlock Complete Access — $6.95"}
                {!loading && <ArrowRight className="w-4 h-4" />}
              </button>

              {/* $6.95 bill image beneath the payment button */}
              <div className="mt-6 flex justify-center">
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/8/85/US_%242_obverse-high.jpg/640px-US_%242_obverse-high.jpg"
                  alt="United States two-dollar bill"
                  loading="lazy"
                  className="max-w-[280px] w-full rounded shadow-[0_10px_30px_-10px_rgba(0,0,0,0.7)] border border-[#D4AF37]/30"
                  data-testid="two-dollar-bill"
                />
              </div>

              <p className="text-xs text-slate-500 text-center mt-4 leading-relaxed">
                Secure checkout via Stripe · Multi-currency · 195+ countries · You&apos;ll be redirected to Stripe&apos;s hosted page.
              </p>
              <p className="text-[11px] text-slate-600 text-center mt-3 leading-relaxed">
                By purchasing you agree to our{" "}
                <Link to="/legal/terms" className="underline hover:text-slate-300">Terms</Link>,{" "}
                <Link to="/legal/privacy" className="underline hover:text-slate-300">Privacy Policy</Link>,{" "}
                <Link to="/legal/disclaimer" className="underline hover:text-slate-300">Disclaimer</Link>, and{" "}
                <Link to="/legal/refund" className="underline hover:text-slate-300">Refund Policy</Link>.
              </p>
            </div>
          </div>
        </div>

        <div className="border-y border-white/5 -mx-6 px-6 py-14">
          <div className="max-w-6xl mx-auto">
            <div className="overline mb-2">What you unlock</div>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight mb-10">
              Six core capabilities, one payment.
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 rounded-md overflow-hidden border border-white/5">
              {BENEFITS.map((b) => {
                const Icon = b.icon;
                return (
                  <div key={b.title} className="p-7 bg-[#0A1128]" data-testid={`benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`}>
                    <Icon className="w-5 h-5 text-[#7DA3D9]" />
                    <h3 className="font-display text-xl mt-4">{b.title}</h3>
                    <p className="text-slate-400 text-sm mt-2 leading-relaxed">{b.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="text-center mt-14">
          <p className="text-slate-400 text-sm">Questions? Call <span className="text-[#B8D0EE] notranslate">(310) 905-7115</span></p>
        </div>
      </div>
    </div>
  );
}
